import { useEffect, useMemo, useState } from 'react';
import { Button, Descriptions, Image, Space, Tag, Typography, List, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CCI_DECLARATION_TEXT,
  FITNESS_QUESTION_IDS,
  checklistItemLabel,
  countFailedItems,
  downloadInspectionPdf,
  expandAfterChecklistItems,
  expandBeforeChecklistItems,
  formatDateTime,
  formatTime,
  getSignedUrl,
  isFitnessPassing,
  parseFitnessAnswers,
  supabase,
  type ChecklistViewRow,
  type InspectionBodyDefect,
  type InspectionChecklistItemRow,
  type VehicleInspection,
} from '@ubus/shared';
import EntityDrawer from './EntityDrawer';

const { Text, Paragraph } = Typography;

type InspectionRow = VehicleInspection & {
  drivers?: { name: string };
  vehicles?: {
    fleet_number?: string | null;
    model?: string | null;
    registration_plate?: string | null;
  } | null;
};

type ConfirmedTripLabel = {
  id: string;
  label: string;
  date?: string | null;
};

function formatInspectionVehicle(row: InspectionRow): string {
  const v = row.vehicles;
  if (v) {
    const plate = v.registration_plate?.trim();
    const fleet = v.fleet_number?.trim();
    if (plate && fleet) return `${plate} (${fleet})`;
    if (plate || fleet) return plate || fleet || '—';
    if (v.model?.trim()) return v.model.trim();
  }
  const rego = row.work_fields?.rego?.trim();
  return rego || '—';
}

function shortTripId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

interface InspectionDetailDrawerProps {
  open: boolean;
  record: InspectionRow | null;
  onClose: () => void;
}

function severityColor(v: string | null | undefined) {
  if (v === 'blocked') return 'error';
  if (v === 'pass_with_notes') return 'warning';
  if (v === 'pass') return 'success';
  return 'default';
}

async function resolvePhotoUrl(path: string | undefined): Promise<string | null> {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('blob:')) return path;
  return getSignedUrl('uploads', path, 3600);
}

function checklistStatusTagColor(status: ChecklistViewRow['status']): string {
  if (status === 'pass') return 'success';
  if (status === 'fail') return 'error';
  if (status === 'na') return 'default';
  return 'warning';
}

function InspectionDetailDrawer({ open, record, onClose }: InspectionDetailDrawerProps) {
  const { t } = useTranslation();
  const [photoMap, setPhotoMap] = useState<Record<string, string>>({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const [confirmedTrips, setConfirmedTrips] = useState<ConfirmedTripLabel[]>([]);
  const [confirmedTripsLoading, setConfirmedTripsLoading] = useState(false);
  const [showAfterFull, setShowAfterFull] = useState(false);
  const [showBeforeFull, setShowBeforeFull] = useState(false);

  const fails = useMemo(
    () => (record?.items ?? []).filter((i) => i.status === 'fail'),
    [record]
  );
  const afterFails = useMemo(
    () => (record?.after_items ?? []).filter((i) => i.status === 'fail'),
    [record]
  );
  const beforeRows = useMemo(
    () => expandBeforeChecklistItems(record?.items),
    [record]
  );
  const afterRows = useMemo(
    () => expandAfterChecklistItems(record?.after_items),
    [record]
  );
  const afterStarted = useMemo(
    () => (record?.after_items?.length ?? 0) > 0 || !!record?.after_completed_at,
    [record]
  );

  useEffect(() => {
    setShowAfterFull(false);
    setShowBeforeFull(false);
  }, [record?.id, open]);

  const handleDownloadPdf = async () => {
    if (!record) return;
    setPdfLoading(true);
    try {
      await downloadInspectionPdf(record);
      message.success(t('operations.inspectionDetail.pdfReady'));
    } catch (err) {
      console.error('[InspectionDetailDrawer] pdf', err);
      message.error(
        err instanceof Error ? err.message : t('operations.inspectionDetail.pdfFailed')
      );
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !record) {
      setPhotoMap({});
      return;
    }
    let cancelled = false;
    (async () => {
      const next: Record<string, string> = {};
      const paths = [
        ...(record.photo_urls ?? []),
        ...((record.body_defects ?? []) as InspectionBodyDefect[])
          .map((d) => d.photo_url)
          .filter(Boolean),
      ] as string[];
      await Promise.all(
        paths.map(async (p) => {
          const url = await resolvePhotoUrl(p);
          if (url) next[p] = url;
        })
      );
      if (!cancelled) setPhotoMap(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, record]);

  useEffect(() => {
    if (!open || !record) {
      setConfirmedTrips([]);
      return;
    }
    const ids = (record.confirmed_trip_ids ?? []).filter(Boolean);
    if (!ids.length) {
      setConfirmedTrips([]);
      return;
    }
    let cancelled = false;
    setConfirmedTripsLoading(true);
    void (async () => {
      try {
        const { data, error } = await supabase
          .from('trips')
          .select('id, trip_number, date, pickup_time, pickup_address, status')
          .in('id', ids);
        if (error) throw error;
        const byId = new Map((data ?? []).map((row) => [row.id as string, row]));
        const labels: ConfirmedTripLabel[] = ids.map((id) => {
          const row = byId.get(id);
          if (!row) {
            return { id, label: shortTripId(id) };
          }
          const num = (row.trip_number as string | null)?.trim() || shortTripId(id);
          const date = row.date ? String(row.date) : '';
          const time = row.pickup_time ? formatTime(String(row.pickup_time)) : '';
          const parts = [num, date, time].filter(Boolean);
          return { id, label: parts.join(' · '), date: date || null };
        });
        if (!cancelled) setConfirmedTrips(labels);
      } catch (err) {
        console.error('[InspectionDetailDrawer] confirmed trips', err);
        if (!cancelled) {
          setConfirmedTrips(ids.map((id) => ({ id, label: shortTripId(id) })));
        }
      } finally {
        if (!cancelled) setConfirmedTripsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, record]);

  const wf = record?.work_fields ?? {};
  const fitness = useMemo(
    () => parseFitnessAnswers(record?.work_fields?.fitness),
    [record]
  );

  return (
    <EntityDrawer
      open={open}
      onClose={onClose}
      title={t('operations.inspectionDetail.title')}
      width={640}
    >
      {!record ? null : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={pdfLoading}
            onClick={() => {
              void handleDownloadPdf();
            }}
          >
            {t('operations.inspectionDetail.downloadPdf')}
          </Button>

          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label={t('operations.columns.vehicle')}>
              {formatInspectionVehicle(record)}
            </Descriptions.Item>
            <Descriptions.Item label={t('operations.columns.driver')}>
              {record.drivers?.name || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('operations.columns.severity')}>
              {record.severity_result ? (
                <Tag color={severityColor(record.severity_result)}>
                  {t(`operations.severity.${record.severity_result}`, {
                    defaultValue: record.severity_result,
                  })}
                </Tag>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('operations.columns.declaredAt')}>
              {record.declaration_accepted_at
                ? formatDateTime(record.declaration_accepted_at)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('operations.inspectionDetail.declarationVer')}>
              {record.declaration_text_ver || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('operations.columns.fails')}>
              {countFailedItems(record.items)}
            </Descriptions.Item>
            <Descriptions.Item label={t('operations.columns.afterDone')}>
              {record.after_completed_at ? (
                <Tag color="success">{t('operations.yes')}</Tag>
              ) : (
                <Tag>{t('operations.no')}</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('operations.inspectionDetail.afterCompletedAt')}>
              {record.after_completed_at
                ? formatDateTime(record.after_completed_at)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('operations.inspectionDetail.confirmedTrips')}>
              {confirmedTripsLoading ? (
                <Text type="secondary">{t('common.loading')}</Text>
              ) : confirmedTrips.length === 0 ? (
                '—'
              ) : (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {confirmedTrips.map((trip) => {
                    const params = new URLSearchParams();
                    params.set('trip', trip.id);
                    if (trip.date) params.set('date', trip.date);
                    return (
                      <Link key={trip.id} to={`/trips?${params.toString()}`}>
                        {trip.label}
                      </Link>
                    );
                  })}
                </Space>
              )}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <Text strong>{t('operations.inspectionDetail.workFields')}</Text>
            <Descriptions column={2} size="small" style={{ marginTop: 8 }} bordered>
              <Descriptions.Item label="Date">{wf.date || '—'}</Descriptions.Item>
              <Descriptions.Item label="Rego">{wf.rego || '—'}</Descriptions.Item>
              <Descriptions.Item label="Driver">{wf.driver_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Tour">{wf.tour_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Pick up">{wf.pick_up || '—'}</Descriptions.Item>
              <Descriptions.Item label="Drop off">{wf.drop_off || '—'}</Descriptions.Item>
              <Descriptions.Item label="Start KM">{wf.start_km || '—'}</Descriptions.Item>
              <Descriptions.Item label="Finish KM">{wf.finish_km || '—'}</Descriptions.Item>
              <Descriptions.Item label="Hotel">{wf.hotel_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Fuel">{wf.fuel_level || '—'}</Descriptions.Item>
              <Descriptions.Item label="Depot start">{wf.depot_start_time || '—'}</Descriptions.Item>
              <Descriptions.Item label="Depot finish">{wf.depot_finish_time || '—'}</Descriptions.Item>
            </Descriptions>
          </div>

          <div>
            <Text strong>{t('operations.inspectionDetail.fitnessTitle')}</Text>
            {!fitness ? (
              <Paragraph type="secondary" style={{ marginTop: 8 }}>
                {t('operations.inspectionDetail.fitnessMissing')}
              </Paragraph>
            ) : (
              <>
                <Descriptions column={2} size="small" style={{ marginTop: 8 }} bordered>
                  <Descriptions.Item label={t('operations.inspectionDetail.fitnessStatus')}>
                    {isFitnessPassing(fitness) ? (
                      <Tag color="success">{t('operations.inspectionDetail.fitnessPass')}</Tag>
                    ) : (
                      <Tag color="error">{t('operations.inspectionDetail.fitnessFail')}</Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('operations.inspectionDetail.fitnessFormVer')}>
                    {fitness.form_ver || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('operations.inspectionDetail.fitnessSignedName')}>
                    {fitness.signed_name || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('operations.inspectionDetail.fitnessSignedAt')}>
                    {fitness.signed_at ? formatDateTime(fitness.signed_at) : '—'}
                  </Descriptions.Item>
                </Descriptions>
                <List
                  size="small"
                  style={{ marginTop: 8 }}
                  dataSource={[...FITNESS_QUESTION_IDS]}
                  renderItem={(qid) => {
                    const v = fitness.answers[qid];
                    return (
                      <List.Item>
                        <Space>
                          <Text>{t(`operations.inspectionDetail.fitnessQuestions.${qid}`)}</Text>
                          {v === true ? (
                            <Tag color="success">{t('operations.yes')}</Tag>
                          ) : v === false ? (
                            <Tag color="error">{t('operations.no')}</Tag>
                          ) : (
                            <Tag>{t('operations.inspectionDetail.fitnessUnanswered')}</Tag>
                          )}
                        </Space>
                      </List.Item>
                    );
                  }}
                />
              </>
            )}
          </div>

          <div>
            <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
              <Text strong>{t('operations.inspectionDetail.beforeChecklist')}</Text>
              <Button type="link" size="small" onClick={() => setShowBeforeFull((v) => !v)}>
                {showBeforeFull
                  ? t('operations.inspectionDetail.hideFullBefore')
                  : t('operations.inspectionDetail.viewFullBefore')}
              </Button>
            </Space>
            <Text strong style={{ display: 'block', marginTop: 12 }}>
              {t('operations.inspectionDetail.failedItems')}
            </Text>
            {fails.length === 0 ? (
              <Paragraph type="secondary" style={{ marginTop: 8 }}>
                {t('operations.inspectionDetail.noFails')}
              </Paragraph>
            ) : (
              <List
                size="small"
                style={{ marginTop: 8 }}
                dataSource={fails}
                renderItem={(item: InspectionChecklistItemRow) => (
                  <List.Item>
                    <Space direction="vertical" size={0}>
                      <Text>
                        {checklistItemLabel(item.id)}{' '}
                        <Tag color="error">
                          {t('operations.inspectionDetail.itemStatus.fail')}
                        </Tag>
                      </Text>
                      {item.note ? <Text type="secondary">{item.note}</Text> : null}
                    </Space>
                  </List.Item>
                )}
              />
            )}
            {showBeforeFull ? (
              <>
                <Text strong style={{ display: 'block', marginTop: 12 }}>
                  {t('operations.inspectionDetail.beforeFullList')}
                </Text>
                <List
                  size="small"
                  style={{ marginTop: 8 }}
                  dataSource={beforeRows}
                  renderItem={(item: ChecklistViewRow) => (
                    <List.Item>
                      <Space direction="vertical" size={0}>
                        <Text>
                          {checklistItemLabel(item.id)}{' '}
                          <Tag color={checklistStatusTagColor(item.status)}>
                            {t(`operations.inspectionDetail.itemStatus.${item.status}`)}
                          </Tag>
                        </Text>
                        {item.note ? <Text type="secondary">{item.note}</Text> : null}
                      </Space>
                    </List.Item>
                  )}
                />
              </>
            ) : null}
          </div>

          <div>
            <Text strong>{t('operations.inspectionDetail.bodyDefects')}</Text>
            {(record.body_defects ?? []).length === 0 ? (
              <Paragraph type="secondary" style={{ marginTop: 8 }}>
                {t('operations.inspectionDetail.noDefects')}
              </Paragraph>
            ) : (
              <List
                size="small"
                style={{ marginTop: 8 }}
                dataSource={record.body_defects as InspectionBodyDefect[]}
                renderItem={(d) => (
                  <List.Item>
                    <Space align="start">
                      <Tag>{d.view}</Tag>
                      <div>
                        <div>{d.note || '—'}</div>
                        {d.photo_url && photoMap[d.photo_url] ? (
                          <Image
                            src={photoMap[d.photo_url]}
                            width={120}
                            style={{ marginTop: 8 }}
                          />
                        ) : null}
                      </div>
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </div>

          <div>
            <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
              <Text strong>{t('operations.inspectionDetail.afterChecklist')}</Text>
              {afterStarted ? (
                <Button type="link" size="small" onClick={() => setShowAfterFull((v) => !v)}>
                  {showAfterFull
                    ? t('operations.inspectionDetail.hideFullAfter')
                    : t('operations.inspectionDetail.viewFullAfter')}
                </Button>
              ) : null}
            </Space>
            {!afterStarted ? (
              <Paragraph type="secondary" style={{ marginTop: 8 }}>
                {t('operations.inspectionDetail.afterNotStarted')}
              </Paragraph>
            ) : (
              <>
                <Descriptions column={2} size="small" style={{ marginTop: 8 }} bordered>
                  <Descriptions.Item label={t('operations.inspectionDetail.finishKm')}>
                    {wf.finish_km || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('operations.inspectionDetail.depotFinish')}>
                    {wf.depot_finish_time || '—'}
                  </Descriptions.Item>
                </Descriptions>
                <Text strong style={{ display: 'block', marginTop: 12 }}>
                  {t('operations.inspectionDetail.afterFails')}
                </Text>
                {afterFails.length === 0 ? (
                  <Paragraph type="secondary" style={{ marginTop: 8 }}>
                    {t('operations.inspectionDetail.noFails')}
                  </Paragraph>
                ) : (
                  <List
                    size="small"
                    style={{ marginTop: 8 }}
                    dataSource={afterFails}
                    renderItem={(item: InspectionChecklistItemRow) => (
                      <List.Item>
                        <Space direction="vertical" size={0}>
                          <Text>
                            {checklistItemLabel(item.id)}{' '}
                            <Tag color="error">
                              {t('operations.inspectionDetail.itemStatus.fail')}
                            </Tag>
                          </Text>
                          {item.note ? <Text type="secondary">{item.note}</Text> : null}
                        </Space>
                      </List.Item>
                    )}
                  />
                )}
                {showAfterFull ? (
                  <>
                    <Text strong style={{ display: 'block', marginTop: 12 }}>
                      {t('operations.inspectionDetail.afterFullList')}
                    </Text>
                    <List
                      size="small"
                      style={{ marginTop: 8 }}
                      dataSource={afterRows}
                      renderItem={(item: ChecklistViewRow) => (
                        <List.Item>
                          <Space direction="vertical" size={0}>
                            <Text>
                              {checklistItemLabel(item.id)}{' '}
                              <Tag color={checklistStatusTagColor(item.status)}>
                                {t(`operations.inspectionDetail.itemStatus.${item.status}`)}
                              </Tag>
                            </Text>
                            {item.note ? <Text type="secondary">{item.note}</Text> : null}
                          </Space>
                        </List.Item>
                      )}
                    />
                  </>
                ) : null}
              </>
            )}
          </div>

          <div>
            <Text strong>{t('operations.inspectionDetail.declaration')}</Text>
            <Paragraph type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
              {CCI_DECLARATION_TEXT}
            </Paragraph>
            {record.notes ? (
              <Paragraph>
                <Text type="secondary">{t('operations.columns.notes')}: </Text>
                {record.notes}
              </Paragraph>
            ) : null}
          </div>
        </Space>
      )}
    </EntityDrawer>
  );
}

export default InspectionDetailDrawer;
